import { Form } from "@unform/web";
import {
  Contact,
  Download,
  Files,
  Mail,
  MessageSquare,
  NotebookPen,
  Send,
  Ticket,
  TicketsPlane,
  X,
} from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import api from "~/services/api";
import { Zoom, toast } from "react-toastify";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { getRegistries } from "~/functions";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { FullGridContext } from "../..";
import FindGeneric from "~/components/Finds/FindGeneric";
import Comments from "~/pages/Financial/Inbounds/Recurrence/Preview/Comments";
import Input from "~/components/RegisterForm/Input";
import DatePicker from "~/components/RegisterForm/DatePicker";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import { preArrivalsStatusOptions } from "~/functions/selectPopoverOptions";
import FileInputMultiple from "~/components/RegisterForm/FileInputMultiple";
import FileInput from "~/components/RegisterForm/FileInput";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { app } from "~/services/firebase";
import { format, parseISO } from "date-fns";
import { Scope } from "@unform/core";
import { AlertContext } from "~/App";
import Textarea from "~/components/RegisterForm/Textarea";
import {
  getFilenameFromContentDisposition,
  openBlobInNewTab,
} from "~/utils/blobPreview";

export const InputContext = createContext({});

const safeParseDate = (value) => {
  if (!value || typeof value !== "string") return null;

  const iso = value.includes("T") ? value : value.replace(" ", "T");
  const parsed = parseISO(iso);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const {
    handleOpened,
    setOpened,
    successfullyUpdated,
    setSuccessfullyUpdated,
  } = useContext(FullGridContext);
  const [loading, setLoading] = useState(false);
  const [pageData, setPageData] = useState({
    loaded: false,
    student: {
      id: null,
      name: "",
      last_name: "",
    },
    processes: [],
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const [documents, setDocuments] = useState([]);
  const [deleteFiles, setDeleteFiles] = useState([]);
  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const { alertBox } = useContext(AlertContext);
  const generalForm = useRef();
  const isActivated =
    String(pageData?.contract?.status || "").toLowerCase() === "active";

  async function getPageData() {
    if (id !== "new") {
      try {
        const { data } = await api.get(`/arrivals/${id}`);
        const { data: dataDocuments } = await api.get(
          `/documents?type=Pre-Arrivals`
        );
        setDocuments(dataDocuments?.rows);

        const { data: dataStudent } = await api.get(
          `/students/${data.student_id}`
        );

        const { enrollment_id } = dataStudent?.contracts[0];

        let dataEnrollment = null;
        if (enrollment_id) {
          const { data } = await api.get(`/enrollments/${enrollment_id}`);
          dataEnrollment = data;
        }

        setPageData({
          ...data,
          student: dataStudent,
          enrollment: dataEnrollment,
          loaded: true,
        });

        const {
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        } = data;
        const registries = await getRegistries({
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        });
        setRegistry(registries);
      } catch (err) {
        console.log(err);
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
      }
    } else {
      setPageData({
        ...pageData,
        loaded: true,
      });
    }
  }

  useEffect(() => {
    getPageData();
  }, []);

  async function handleGeneralFormSubmit(data) {
    const uploadedFiles = [];
    setLoading(true);
    try {
      if (data.files) {
        for (const fileLine of data.files) {
          if (!fileLine.files || fileLine.files.length === 0) continue;
          if (fileLine.files.length > 0) {
            for (const file of fileLine.files) {
              await uploadFile(file, fileLine.document_id);
            }
          } else {
            await uploadFile(fileLine.files, fileLine.document_id);
          }
        }
        data.files = uploadedFiles;
      }
      if (data.notice_date) {
        data.notice_date = format(data.notice_date, "yyyy-MM-dd");
      }
      if (deleteFiles.length > 0) {
        data.deleteFiles = deleteFiles;
      }
      if (successfullyUpdated) {
        toast("No need to be saved!", {
          autoClose: 1000,
          type: "info",
          transition: Zoom,
        });
        return;
      }
      await api
        .put(`/pre-arrivals/${id}`, data)
        .catch((err) => {
          toast(err.response.data.error, {
            type: "error",
            autoClose: 3000,
          });
        })
        .then(() => {
          toast("Pre-Arrival updated successfully!", {
            type: "success",
            autoClose: 2000,
          });
          setSuccessfullyUpdated(true);
          handleOpened(null);
        });
    } catch (err) {
      console.log(err);
      // toast(err.response.data.error, {
      //   type: "error",
      //   autoClose: 3000,
      // });
    } finally {
      setLoading(false);
    }
    async function uploadFile(file, document_id) {
      const { name, size } = file;
      const fileUuid = uuidv4();
      const storage = getStorage(app);
      const local = "PreArrivals/Files/" + fileUuid + ".png";
      const imageRef = ref(storage, local);
      await uploadBytes(imageRef, file).then(async (snapshot) => {
        await getDownloadURL(snapshot.ref).then(async (downloadURL) => {
          file.downloadURL = downloadURL;
          uploadedFiles.push({
            name,
            size,
            downloadURL,
            key: fileUuid + ".png",
            document_id,
          });
        });
      });
    }
  }

  async function handleSendPlacementTestLink(enrollment_id) {
    setLoading(true);
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Are you sure you want to send the placement test link to the student?",
      buttons: [
        {
          title: "No",
          class: "cancel",
          onPress: () => {
            setLoading(false);
          },
        },
        {
          title: "Yes",
          onPress: async () => {
            await api
              .post(`/enrollments/start-placement-test/`, {
                enrollment_id,
              })
              .catch((err) => {
                toast(err.response.data.error, {
                  type: "error",
                  autoClose: 3000,
                });
              })
              .then(() => {
                toast("E-mail sent to the student!", {
                  autoClose: 1000,
                });
              })
              .finally(() => {
                setLoading(false);
              });
          },
        },
      ],
    });
  }

  const getWelcomeLetterPayload = () => {
    const payload = {};
    if (pageData?.student?.studentgroup) {
      payload.studentgroup = pageData.student.studentgroup;
    }
    const rawDate = pageData?.student?.start_date || pageData?.enrollment?.plan_date;
    const parsedDate = safeParseDate(rawDate);
    if (parsedDate) {
      payload.date = format(parsedDate, "yyyy-MM-dd");
    }
    return payload;
  };

  const handleSendWelcomeLetter = async () => {
    if (!pageData?.student?.id) return;
    setLoading(true);
    alertBox({
      title: "Attention!",
      descriptionHTML: "Do you want to send the student a welcome letter?",
      buttons: [
        {
          title: "No",
          class: "cancel",
          onPress: () => setLoading(false),
        },
        {
          title: "Yes",
          onPress: async () => {
            try {
              const { data } = await api.post(
                `/send-welcome-letter/${pageData.student.id}`,
                getWelcomeLetterPayload()
              );
              toast(data?.message || "Email sent!", { autoClose: 3000 });
            } catch (err) {
              toast(err?.response?.data?.error || "Failed to send welcome letter", {
                type: "error",
                autoClose: 3000,
              });
            } finally {
              setLoading(false);
            }
          },
        },
      ],
    });
  };

  const handlePrintWelcomeLetter = async () => {
    if (!pageData?.student?.id) return;
    try {
      const response = await api.get(
        `/welcome-letter/pdf/${pageData.student.id}`,
        { responseType: "blob" }
      );
      const filename =
        getFilenameFromContentDisposition(response.headers["content-disposition"]) ||
        `welcome_letter_${Date.now()}.pdf`;
      const pdfBlob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });
      const opened = openBlobInNewTab(pdfBlob, {
        filename: filename.endsWith(".pdf") ? filename : `${filename}.pdf`,
      });
      if (!opened) {
        toast("Pop-up blocked. Allow pop-ups to preview the PDF.", {
          type: "error",
          autoClose: 3000,
        });
      }
    } catch (err) {
      toast(err?.response?.data?.error || "Failed to generate PDF", {
        type: "error",
        autoClose: 3000,
      });
    }
  };

  const handlePrintMilaAppFirstAccess = async () => {
    if (!pageData?.student?.id) return;
    try {
      const response = await api.get(
        `/mila-app-first-access/pdf/${pageData.student.id}`,
        { responseType: "blob" }
      );
      const filename =
        getFilenameFromContentDisposition(response.headers["content-disposition"]) ||
        `mila_app_first_access_${Date.now()}.pdf`;
      const pdfBlob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });
      const opened = openBlobInNewTab(pdfBlob, {
        filename: filename.endsWith(".pdf") ? filename : `${filename}.pdf`,
      });
      if (!opened) {
        toast("Pop-up blocked. Allow pop-ups to preview the PDF.", {
          type: "error",
          autoClose: 3000,
        });
      }
    } catch (err) {
      toast(err?.response?.data?.error || "Failed to generate PDF", {
        type: "error",
        autoClose: 3000,
      });
    }
  };

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  function handleDeleteFile(id) {
    setDeleteFiles([...deleteFiles, id]);
    setSuccessfullyUpdated(false);
    setPageData({
      ...pageData,
      files: pageData.files.filter((file) => file.id !== id),
    });
  }

  const isTransfer = pageData.contract?.processsubstatuses?.name === "Transfer";
  const isInitial =
    pageData.contract?.processsubstatuses?.name === "Initial Visa";
  const isCOS =
    pageData.contract?.processsubstatuses?.name === "Change of Status";
  const isReinstatement =
    pageData.contract?.processsubstatuses?.name === "Reinstatement";

  const process = pageData?.processes[0];

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        <div className="flex h-full flex-row items-start justify-between gap-4">
          <div className="flex flex-col items-center justify-between text-xs w-40 gap-4">
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="general"
            >
              <TicketsPlane size={16} /> General
            </RegisterFormMenu>
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="processes"
            >
              <Ticket size={16} /> Last Processes
            </RegisterFormMenu>
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="placement-test"
            >
              <NotebookPen size={16} /> Placement Test
            </RegisterFormMenu>
            {isActivated && (
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="welcome-letter"
              >
                <Mail size={16} /> Welcome Letter
              </RegisterFormMenu>
            )}
            {pageData?.enrollment?.id && (
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="comments"
              >
                <MessageSquare size={16} /> Enrollment Comments
              </RegisterFormMenu>
            )}
          </div>
          <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
            <InputContext.Provider
              value={{
                id,
                generalForm,
                setSuccessfullyUpdated,
                fullscreen,
                setFullscreen,
                successfullyUpdated,
                handleCloseForm,
                canceled: pageData.canceled_at,
              }}
            >
              <div
                id="scrollPage"
                className="flex flex-col items-start justify-start text-sm overflow-y-scroll"
              >
                <Form
                  ref={generalForm}
                  onSubmit={handleGeneralFormSubmit}
                  className="w-full"
                >
                  <FormHeader
                    access={access}
                    loading={loading}
                    title={
                      pageData.student.name + " " + pageData.student.last_name
                    }
                    registry={registry}
                    InputContext={InputContext}
                  />
                  {id === "new" || pageData.loaded ? (
                    <>
                      {activeMenu === "general" && (
                        <InputLineGroup
                          title="GENERAL"
                          activeMenu={activeMenu === "general"}
                        >
                          <FindGeneric
                            name="processtypes"
                            title="Process Type"
                            scope="processtypes"
                            readOnly
                            InputContext={InputContext}
                            defaultValue={{
                              id: pageData.contract?.processtypes?.id,
                              name: pageData.contract?.processtypes?.name,
                            }}
                            fields={[
                              {
                                title: "Name",
                                name: "name",
                              },
                            ]}
                          />
                          <FindGeneric
                            name="processsubstatuses"
                            title="Process Sub Status"
                            scope="processsubstatuses"
                            readOnly
                            InputContext={InputContext}
                            defaultValue={{
                              id: pageData.contract?.processsubstatuses?.id,
                              name: pageData.contract?.processsubstatuses?.name,
                            }}
                            fields={[
                              {
                                title: "Name",
                                name: "name",
                              },
                            ]}
                          />
                          <InputLine title="Placement Test">
                            <Input
                              type="text"
                              name="placement_test_status"
                              readOnly
                              grow
                              title="Status"
                              defaultValue={
                                pageData?.enrollment?.placement_test_status ||
                                "Not started"
                              }
                              InputContext={InputContext}
                            />
                            {pageData?.enrollment?.placement_test_status.toUpperCase() ===
                              "NOT STARTED" ||
                            !pageData?.enrollment?.placement_test_status ? (
                              <>
                                {pageData?.enrollment?.id && (
                                  <button
                                    type="button"
                                    className="px-4 py-2 rounded-md mt-4 flex flex-row items-center justify-center text-sm border border-red-500 text-red-500 gap-1 hover:border-primary hover:text-primary cursor-pointer"
                                    onClick={() =>
                                      handleSendPlacementTestLink(
                                        pageData?.enrollment?.id
                                      )
                                    }
                                  >
                                    Resend placement test link
                                    <Send size={14} />
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                <Input
                                  type="text"
                                  name="placement_test_score"
                                  readOnly
                                  grow
                                  title="Placement Test Score"
                                  defaultValue={
                                    pageData?.enrollment?.placement_test_score
                                  }
                                  InputContext={InputContext}
                                />
                                <Input
                                  type="text"
                                  name="final_level"
                                  readOnly
                                  grow
                                  title="Final Level"
                                  defaultValue={
                                    pageData?.enrollment?.final_level
                                  }
                                  InputContext={InputContext}
                                />
                                <DatePicker
                                  name="appointment_date"
                                  grow
                                  readOnly
                                  title="Appointment Date"
                                defaultValue={
                                  pageData?.enrollment?.appointment_date
                                    ? safeParseDate(
                                        pageData?.enrollment?.appointment_date
                                      )
                                    : null
                                }
                                  placeholderText="MM/DD/YYYY"
                                  InputContext={InputContext}
                                />
                              </>
                            )}
                          </InputLine>
                          <InputLine title="Student's Plan">
                            <DatePicker
                              name="plan_date"
                              grow
                              readOnly
                              title="Start Date"
                              defaultValue={
                                pageData?.student?.start_date
                                  ? safeParseDate(pageData?.student?.start_date)
                                  : pageData?.enrollment?.plan_date
                                  ? safeParseDate(pageData?.enrollment?.plan_date)
                                  : null
                              }
                              placeholderText="MM/DD/YYYY"
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="plan_schedule"
                              grow
                              readOnly
                              title="Schedule"
                              defaultValue={pageData?.enrollment?.plan_schedule}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="plan_months"
                              grow
                              readOnly
                              title="Months"
                              defaultValue={pageData?.enrollment?.plan_months}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="I-20 Details">
                            <DatePicker
                              name="program_start_date"
                              grow
                              readOnly
                              title="Program Start Date"
                              defaultValue={
                                pageData.enrollment?.student?.programs[0]
                                  ?.start_date
                                  ? safeParseDate(
                                      pageData.enrollment?.student?.programs[0]
                                        ?.start_date
                                    )
                                  : null
                              }
                              placeholderText="MM/DD/YYYY"
                              InputContext={InputContext}
                            />
                            <DatePicker
                              name="program_end_date"
                              grow
                              readOnly
                              title="Program End Date"
                              defaultValue={
                                pageData.enrollment?.student?.programs[0]
                                  ?.end_date
                                  ? safeParseDate(
                                      pageData.enrollment?.student?.programs[0]
                                        ?.end_date
                                    )
                                  : null
                              }
                              placeholderText="MM/DD/YYYY"
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="General Data">
                            {isInitial && (
                              <DatePicker
                                name="interview_date"
                                grow
                                readOnly
                                title="Interview Date"
                                defaultValue={
                                  process?.interview_date
                                    ? safeParseDate(process?.interview_date)
                                    : null
                                }
                                placeholderText="MM/DD/YYYY"
                                InputContext={InputContext}
                              />
                            )}
                            {isCOS && (
                              <>
                                <Input
                                  type="text"
                                  name="case_number"
                                  grow
                                  readOnly
                                  title="Case Number"
                                  defaultValue={process?.case_number}
                                  InputContext={InputContext}
                                />
                                <DatePicker
                                  name="notice_date"
                                  grow
                                  readOnly
                                  title="Notice Date"
                                defaultValue={
                                  process?.notice_date
                                    ? safeParseDate(process?.notice_date)
                                    : null
                                }
                                  placeholderText="MM/DD/YYYY"
                                  InputContext={InputContext}
                                />
                              </>
                            )}
                            <SelectPopover
                              name="status"
                              grow
                              title="Status"
                              options={preArrivalsStatusOptions}
                              isSearchable
                              readOnly
                              defaultValue={preArrivalsStatusOptions.find(
                                (opt) =>
                                  opt.value ===
                                  (process?.status || "Processing")
                              )}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          {documents?.length > 0 &&
                            documents.map((document, index) => {
                              if (
                                document.type !==
                                pageData.contract?.processsubstatuses?.name
                              ) {
                                return null;
                              }
                              return (
                                <Scope key={index} path={`files[${index}]`}>
                                  <InputLine title={document.title}>
                                    {pageData.files &&
                                      pageData.files.length > 0 && (
                                        <div className="w-full flex flex-row items-center justify-start flex-wrap gap-2">
                                          {pageData.files
                                            .filter(
                                              (file) =>
                                                !deleteFiles.includes(file.id)
                                            )
                                            .filter(
                                              (file) =>
                                                file.document_id === document.id
                                            )
                                            .map((file, index) => (
                                              <div
                                                key={index}
                                                className="flex flex-row items-center justify-start border rounded-md  px-2 gap-1 cursor-normal"
                                              >
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    handleDeleteFile(file.id)
                                                  }
                                                  className="text-xs text-red-700 cursor-pointer flex flex-row items-center justify-start gap-1 px-1 mx-1 py-1 rounded-sm hover:bg-red-100"
                                                >
                                                  <X size={12} />{" "}
                                                </button>
                                                <a
                                                  href={file.file.url}
                                                  target="_blank"
                                                  className="text-xs w-full sm:w-auto"
                                                >
                                                  <div className="flex flex-row items-center py-2 gap-1 truncate">
                                                    <Files
                                                      size={16}
                                                      className="flex-shrink-0"
                                                    />
                                                    <span className="truncate">
                                                      {file.file.name}
                                                    </span>
                                                  </div>
                                                </a>
                                              </div>
                                            ))}
                                        </div>
                                      )}
                                  </InputLine>
                                </Scope>
                              );
                            })}
                        </InputLineGroup>
                      )}
                      {activeMenu === "processes" && (
                        <InputLineGroup
                          title="PROCESSES"
                          activeMenu={activeMenu === "processes"}
                        >
                          {pageData?.processes.map((process, index) => {
                            const parsedUpdatedAt = safeParseDate(
                              process.updated_at
                            );
                            return (
                              <InputLine
                                key={index}
                                title={
                                  parsedUpdatedAt
                                    ? format(parsedUpdatedAt, "MMMM do, yyyy")
                                    : ""
                                }
                              >
                                {isInitial && (
                                  <DatePicker
                                    name="interview_date"
                                    grow
                                    readOnly
                                    title="Interview Date"
                                      defaultValue={
                                        process?.interview_date
                                          ? safeParseDate(
                                              process?.interview_date
                                            )
                                          : null
                                      }
                                    placeholderText="MM/DD/YYYY"
                                    InputContext={InputContext}
                                  />
                                )}
                                {isCOS && (
                                  <>
                                    <Input
                                      type="text"
                                      name="case_number"
                                      grow
                                      readOnly
                                      title="Case Number"
                                      defaultValue={process?.case_number}
                                      InputContext={InputContext}
                                    />
                                    <DatePicker
                                      name="notice_date"
                                      grow
                                      readOnly
                                      title="Notice Date"
                                      defaultValue={
                                        process?.notice_date
                                          ? safeParseDate(process?.notice_date)
                                          : null
                                      }
                                      placeholderText="MM/DD/YYYY"
                                      InputContext={InputContext}
                                    />
                                  </>
                                )}
                                <SelectPopover
                                  name="status"
                                  grow
                                  readOnly
                                  title="Status"
                                  options={preArrivalsStatusOptions}
                                  isSearchable
                                  defaultValue={preArrivalsStatusOptions.find(
                                    (opt) =>
                                      opt.value ===
                                      (process?.status || "Processing")
                                  )}
                                  InputContext={InputContext}
                                />
                              </InputLine>
                            );
                          })}
                        </InputLineGroup>
                      )}
                      {activeMenu === "placement-test" && (
                        <InputLineGroup
                          title="placement-test"
                          activeMenu={activeMenu === "placement-test"}
                        >
                          {console.log(pageData.contract)}
                          <Scope path="enrollment">
                            <InputLine title="Placement Test">
                              <Input
                                type="text"
                                name="question_level"
                                grow
                                readOnly
                                title="Question Level"
                                defaultValue={
                                  pageData?.contract?.enrollment?.question_level
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="reading_score_percentage"
                                grow
                                readOnly
                                title="Reading Score %"
                                defaultValue={
                                  pageData?.contract?.enrollment
                                    ?.reading_score_percentage
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="writing_score"
                                grow
                                readOnly
                                title="Writing Score"
                                defaultValue={
                                  pageData?.contract?.enrollment?.writing_score
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="writing_score_percentage"
                                grow
                                readOnly
                                title="Writing Score %"
                                defaultValue={
                                  pageData?.contract?.enrollment
                                    ?.writing_score_percentage
                                }
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine>
                              <Input
                                type="text"
                                name="reading_writing_score"
                                grow
                                readOnly
                                title="Reading Writing Score %"
                                defaultValue={
                                  pageData?.contract?.enrollment
                                    ?.reading_writing_score
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="speaking_score"
                                grow
                                readOnly
                                title="Speaking Score"
                                defaultValue={
                                  pageData?.contract?.enrollment?.speaking_score
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="speaking_score_percentage"
                                grow
                                readOnly
                                title="Speaking Score %"
                                defaultValue={
                                  pageData?.contract?.enrollment
                                    ?.speaking_score_percentage
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="speaking_level"
                                grow
                                readOnly
                                title="Speaking Level"
                                defaultValue={
                                  pageData?.contract?.enrollment?.speaking_level
                                }
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine>
                              <Input
                                type="text"
                                name="academic_decision"
                                grow
                                readOnly
                                title="Academic Overall Decision"
                                defaultValue={
                                  pageData?.contract?.enrollment
                                    ?.academic_decision
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="final_level"
                                grow
                                readOnly
                                title="Final Level"
                                defaultValue={
                                  pageData?.contract?.enrollment?.final_level
                                }
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine>
                              <Textarea
                                name="academic_notes"
                                grow
                                readOnly
                                rows={3}
                                title="Academic Notes"
                                defaultValue={
                                  pageData?.contract?.enrollment?.academic_notes
                                }
                                InputContext={InputContext}
                              />
                              <Textarea
                                name="academic_feedback"
                                grow
                                readOnly
                                rows={3}
                                title="Academic Feedback"
                                defaultValue={
                                  pageData?.contract?.enrollment
                                    ?.academic_feedback
                                }
                                InputContext={InputContext}
                              />
                            </InputLine>
                          </Scope>
                        </InputLineGroup>
                      )}
                      {activeMenu === "welcome-letter" && (
                        <InputLineGroup
                          title="Welcome Letter"
                          activeMenu={activeMenu === "welcome-letter"}
                        >
                          <div className="flex flex-1 w-full flex-col items-start justify-start text-center gap-4 px-4">
                            <div className="flex w-full flex-row items-center justify-start text-center gap-4 border border-gray-200 bg-slate-50 rounded-md p-4">
                              <button
                                type="button"
                                disabled={loading}
                                onClick={handleSendWelcomeLetter}
                                className="bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2"
                              >
                                <Send size={14} />
                                <strong>Send Welcome Letter</strong>
                              </button>
                              <button
                                type="button"
                                disabled={loading}
                                onClick={handlePrintWelcomeLetter}
                                className="bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2"
                              >
                                <Download size={14} />
                                <strong>Print Student Handbook</strong>
                              </button>
                              <button
                                type="button"
                                disabled={loading}
                                onClick={handlePrintMilaAppFirstAccess}
                                className="bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2"
                              >
                                <Download size={14} />
                                <strong>Print MILA App First Access</strong>
                              </button>
                            </div>
                          </div>
                        </InputLineGroup>
                      )}
                      <InputLineGroup
                        title="Comments"
                        activeMenu={activeMenu === "comments"}
                      >
                        <Comments enrollment_id={pageData?.enrollment?.id} />
                      </InputLineGroup>
                    </>
                  ) : (
                    <FormLoading />
                  )}
                </Form>
              </div>
            </InputContext.Provider>
          </div>
        </div>
      ) : null}
    </Preview>
  );
}
